import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Typography,
  IconButton,
  Box,
  Paper,
  Stack,
  Button,
} from "@mui/material";
import {
  FaPlusCircle,
  FaTrashAlt,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaUsers,
  FaTag,
  FaCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { setEventDetails } from "../../../../redux/slices/InquirySlice";

const InquiryEvents = ({ formData }) => {
  const dispatch = useDispatch();

  const [events, setEvents] = useState([]); // Completed events only
  const [isAdding, setIsAdding] = useState(true); // Start with form open if no events
  const [currentEvent, setCurrentEvent] = useState({
    eventDate: null,
    eventTime: "",
    eventType: "",
    eventLocation: "",
    totalGuestCount: "",
  });

  // Load saved events from formData
  useEffect(() => {
    if (!formData?.eventDetails) {
      // No saved data → show form open by default
      setIsAdding(true);
      return;
    }

    try {
      const saved = JSON.parse(formData.eventDetails);
      if (Array.isArray(saved) && saved.length > 0) {
        const parsed = saved.map((evt) => ({
          ...evt,
          eventDate: evt.eventDate ? new Date(evt.eventDate) : null,
        }));
        setEvents(parsed);
        // If events exist → start with form closed (user can click + to add more)
        setIsAdding(false);
      } else {
        // Empty array → treat as no events
        setIsAdding(true);
      }
    } catch (error) {
      console.error("Failed to parse eventDetails:", error);
      setIsAdding(true); // fallback: show form
    }
  }, [formData?.eventDetails]);

  // Save completed events to Redux
  useEffect(() => {
    if (!formData?.engagedCouple || formData?.engagedCouple !== 1) return;

    const serialized = events.map((evt) => ({
      ...evt,
      eventDate: evt.eventDate ? evt.eventDate.toISOString() : "",
    }));

    dispatch(setEventDetails(JSON.stringify(serialized)));
  }, [events, dispatch, formData?.engagedCouple]);

  const isCurrentValid =
    currentEvent.eventDate &&
    currentEvent.eventTime &&
    currentEvent.eventType?.trim() &&
    currentEvent.eventLocation?.trim() &&
    currentEvent.totalGuestCount &&
    Number(currentEvent.totalGuestCount) > 0;

  const handleStartAdding = () => {
    setIsAdding(true);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setCurrentEvent({
      eventDate: null,
      eventTime: "",
      eventType: "",
      eventLocation: "",
      totalGuestCount: "",
    });
  };

  const handleSaveEvent = () => {
    if (!isCurrentValid) {
      toast.error("Please fill all fields correctly");
      return;
    }

    setEvents((prev) => [...prev, { ...currentEvent }]);
    toast.success("Event added successfully!");

    // Reset form but keep it open for easy addition of more events
    setCurrentEvent({
      eventDate: null,
      eventTime: "",
      eventType: "",
      eventLocation: "",
      totalGuestCount: "",
    });

    // Do NOT close the form after adding — keeps it open for next event
    // setIsAdding(false); ← Removed intentionally
  };

  const handleRemove = (index) => {
    setEvents((prev) => prev.filter((_, i) => i !== index));
    // If no events left after delete, show form again
    if (events.length === 1) {
      setIsAdding(true);
    }
  };

  const handleChange = (field, value) => {
    setCurrentEvent((prev) => ({ ...prev, [field]: value }));
  };

  const formatDate = (date) => (date ? dayjs(date).format("MMM DD, YYYY") : "");
  const convertTo12Hour = (time24) => {
    if (!time24) return "";
    const [h, m] = time24.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")} ${period}`;
  };

  return (
    <Box className="Profile-Container" sx={{ py: 1 }}>
      {/* Display Saved Events */}
      {events.map((event, index) => (
        <Paper
          key={index}
          elevation={2}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: "#f9f9f9",
            borderRadius: 2,
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => handleRemove(index)}
            color="error"
            size="small"
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <FaTrashAlt />
          </IconButton>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              {event.eventType}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Box display="flex" alignItems="center" gap={1}>
                <FaCalendarAlt color="#1976d2" />
                <Typography variant="body2">
                  {formatDate(event.eventDate)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <FaClock color="#1976d2" />
                <Typography variant="body2">
                  {convertTo12Hour(event.eventTime)}
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Box display="flex" alignItems="center" gap={1}>
                <FaTag color="#1976d2" />
                <Typography variant="body2">{event.eventType}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <FaMapMarkerAlt color="#1976d2" />
                <Typography variant="body2">{event.eventLocation}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <FaUsers color="#1976d2" />
                <Typography variant="body2">
                  {event.totalGuestCount} Guests
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>
      ))}

      {/* Show Input Form when adding (default for first event) */}
      {isAdding && (
        <Box
          sx={{
            border: "1px dashed #ccc",
            borderRadius: 2,
            p: 3,
            backgroundColor: "#fff",
            mt: events.length > 0 ? 2 : 0,
          }}
        >
          {/* <Typography variant="h6" gutterBottom>
            {events.length === 0 ? "Add Your Event" : "Add Another Event"}
          </Typography> */}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Event Date <span style={{ color: "red" }}>*</span>
              </Typography>
              <DatePicker
                selected={currentEvent.eventDate}
                onChange={(date) => handleChange("eventDate", date)}
                minDate={new Date()}
                dateFormat="MMM dd, yyyy"
                placeholderText="Select event date"
                customInput={<TextField fullWidth size="small" />}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Event Time <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                type="time"
                fullWidth
                size="small"
                value={currentEvent.eventTime}
                onChange={(e) => handleChange("eventTime", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Event Type <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Wedding Ceremony, Reception, Mehendi"
                value={currentEvent.eventType}
                onChange={(e) => handleChange("eventType", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Event Location <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="City or Venue name"
                value={currentEvent.eventLocation}
                onChange={(e) => handleChange("eventLocation", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Guest Count <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                placeholder="Number of guests"
                value={currentEvent.totalGuestCount}
                inputProps={{ min: 1 }}
                onChange={(e) =>
                  handleChange("totalGuestCount", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={1}>
                {events.length > 0 && (
                  <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveEvent}
                  disabled={!isCurrentValid}
                  startIcon={<FaCheck />}
                >
                  {events.length === 0 ? "Add Event" : "Add Another"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Show + Button only when events exist and form is closed */}
      {events.length > 0 && !isAdding && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <IconButton onClick={handleStartAdding} color="primary" size="large">
            <FaPlusCircle size={50} />
          </IconButton>
        </Box>
      )}

      {/* Optional: Show subtle placeholder when no events and form closed (shouldn't happen normally) */}
      {events.length === 0 && !isAdding && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: "text.secondary",
          }}
        >
          <Typography variant="body1">No events added yet</Typography>
        </Box>
      )}
    </Box>
  );
};

export default InquiryEvents;
